import { useState } from 'react';
import { useSiteContent, ProjectItem } from '@/context/SiteContentContext';
import ImageUploadField from './ImageUploadField';

const emptyProject = (): ProjectItem => ({
  id: Date.now().toString(),
  title: '',
  category: 'GIS & Surveying',
  description: '',
  image: '',
  client: '',
  year: new Date().getFullYear().toString(),
});

const categoryOptions = ['GIS & Surveying', 'Research & Evaluation', 'IT Services', 'Training & Capacity Building'];

export default function AdminProjectsTab() {
  const { content, updateContent } = useSiteContent();
  const projects = content.projects;
  const [editing, setEditing] = useState<ProjectItem | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const handleSave = () => {
    if (!editing) return;
    if (isNew) {
      updateContent('projects', [...projects, editing]);
    } else {
      updateContent('projects', projects.map((p) => (p.id === editing.id ? editing : p)));
    }
    setEditing(null);
    setIsNew(false);
  };

  const handleDelete = (id: string) => {
    updateContent('projects', projects.filter((p) => p.id !== id));
    setDeleteId(null);
  };

  const handleEdit = (project: ProjectItem) => {
    setEditing({ ...project });
    setIsNew(false);
  };

  const handleNew = () => {
    setEditing(emptyProject());
    setIsNew(true);
  };

  if (editing) {
    return (
      <div className="space-y-5">
        <div className="flex items-center gap-3 border-b border-gray-100 pb-3">
          <button onClick={() => { setEditing(null); setIsNew(false); }} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer transition-colors">
            <i className="ri-arrow-left-line text-gray-600"></i>
          </button>
          <h3 className="text-lg font-bold text-gray-900">{isNew ? 'Add New Project' : 'Edit Project'}</h3>
        </div>

        <ImageUploadField label="Project Image" value={editing.image} onChange={(v) => setEditing({ ...editing, image: v })} />

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Project Title *</label>
          <input type="text" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })}
            placeholder="e.g. Cadastral Survey — Nairobi" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Category</label>
            <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500 cursor-pointer bg-white">
              {categoryOptions.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Year</label>
            <input type="text" value={editing.year} onChange={(e) => setEditing({ ...editing, year: e.target.value })}
              placeholder="2024" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500" />
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Client</label>
          <input type="text" value={editing.client} onChange={(e) => setEditing({ ...editing, client: e.target.value })}
            placeholder="e.g. World Food Programme" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500" />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Description</label>
          <textarea value={editing.description} rows={4} onChange={(e) => setEditing({ ...editing, description: e.target.value })}
            placeholder="Describe the project..." className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500 resize-none" />
        </div>

        <div className="flex gap-3 pt-2">
          <button onClick={() => { setEditing(null); setIsNew(false); }}
            className="flex-1 border border-gray-200 text-gray-700 font-semibold py-3 rounded-lg cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors text-sm">
            Cancel
          </button>
          <button onClick={handleSave} disabled={!editing.title.trim()}
            className="flex-1 bg-teal-600 hover:bg-teal-700 disabled:bg-teal-300 text-white font-semibold py-3 rounded-lg cursor-pointer whitespace-nowrap transition-colors text-sm">
            {isNew ? 'Add Project' : 'Save Changes'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between border-b border-gray-100 pb-3">
        <h3 className="text-lg font-bold text-gray-900">Projects Gallery</h3>
        <button onClick={handleNew}
          className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold px-4 py-2 rounded-lg cursor-pointer whitespace-nowrap transition-colors">
          <i className="ri-add-line"></i> Add Project
        </button>
      </div>

      {projects.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <i className="ri-image-add-line text-4xl mb-3 block"></i>
          <p className="text-sm">No projects yet. Click "Add Project" to get started.</p>
        </div>
      )}

      <div className="space-y-3">
        {projects.map((project) => (
          <div key={project.id} className="flex items-center gap-4 bg-gray-50 rounded-xl p-4 border border-gray-100">
            <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-200 shrink-0">
              {project.image ? (
                <img src={project.image} alt={project.title} className="w-full h-full object-cover object-top" />
              ) : (
                <div className="w-full h-full flex items-center justify-center"><i className="ri-image-line text-gray-400 text-xl"></i></div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-gray-900 text-sm truncate">{project.title}</div>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-teal-600 text-xs font-medium">{project.category}</span>
                <span className="text-gray-300 text-xs">•</span>
                <span className="text-gray-400 text-xs">{project.client}</span>
                <span className="text-gray-300 text-xs">•</span>
                <span className="text-gray-400 text-xs">{project.year}</span>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => handleEdit(project)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-teal-50 text-teal-600 cursor-pointer transition-colors">
                <i className="ri-edit-line text-sm"></i>
              </button>
              <button onClick={() => setDeleteId(project.id)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-red-400 cursor-pointer transition-colors">
                <i className="ri-delete-bin-line text-sm"></i>
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Delete Confirm */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-red-50 mx-auto mb-3">
              <i className="ri-delete-bin-line text-red-500 text-lg"></i>
            </div>
            <h3 className="text-base font-bold text-gray-900 text-center mb-2">Delete Project?</h3>
            <p className="text-gray-500 text-sm text-center mb-5">This project will be removed from the gallery.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 border border-gray-200 text-gray-700 font-semibold py-2.5 rounded-lg cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors text-sm">Cancel</button>
              <button onClick={() => handleDelete(deleteId)} className="flex-1 bg-red-500 hover:bg-red-600 text-white font-semibold py-2.5 rounded-lg cursor-pointer whitespace-nowrap transition-colors text-sm">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
